import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { useUserStore } from '../stores/useUserStore';
import { useLanguageStore } from '../stores/useLanguageStore';
import { useNavigate } from 'react-router-dom';
import authProvider from '../authProvider';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
// Lucide React icons
import {
  LifeBuoy,
  Menu,
  Search,
  User,
  Languages,
  Moon,
  Sun,
} from 'lucide-react';

import { ModernThemeToggle } from './ModernThemeToggle';
import { ModernLanguageSwitcher } from './ModernLanguageSwitcher';

import { NotificationButton } from '../layout/NotificationButton';
import ChangePasswordDialog from '../layout/ChangePasswordDialog';
import PasskeyRegistrationDialog from '../layout/PasskeyRegistrationDialog';
import WizardManager from './WizardManager';
import { BugReportDialog } from './BugReportDialog';
import { RoleSimulationIndicator } from './RoleSimulationIndicator';
import { useSensitiveDataBlur } from '../hooks/useSensitiveDataBlur';
import { cn } from '../lib/utils';
import { QRCodeLoginDialog } from './QRCodeLoginDialog';
import { QRLoginScannerDialog } from './QRLoginScannerDialog';
import { Users, Clock, User as UserIcon } from 'lucide-react';
import { recentUsersService, RecentUser } from '../services/recentUsersService';

const ModernLogo = ({ onClick }: { onClick?: () => void }) => {
  return (
    <div className="flex items-center cursor-pointer" onClick={onClick}>
      <img
        src="/nekopos-logo.svg"
        alt="NEKO POS"
        className="h-20 w-auto dark:invert"
      />
    </div>
  );
};

const ThemeToggle = () => {
  return (
    <div className="flex items-center">
      <ModernThemeToggle />
    </div>
  );
};

const UserMenu = () => {
  const { translate } = useTranslation();
  const { user, fetchUser, logout } = useUserStore();
  const { locale, setLocale } = useLanguageStore();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showPasskeyRegistration, setShowPasskeyRegistration] = useState(false);
  const [showQrLogin, setShowQrLogin] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [recentUsers, setRecentUsers] = useState<RecentUser[]>([]);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    try {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme === 'dark' || savedTheme === 'light') {
        return savedTheme;
      }
      const systemPrefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      return systemPrefersDark ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    // Check if mobile device
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // Fetch user data when component mounts (only if not already loaded)
    if (!user) {
      fetchUser();
    }

    // Load recent users (excluding current user)
    const loadRecentUsers = () => {
      const allRecent = recentUsersService.getRecentUsers();
      const filtered = user
        ? allRecent.filter((u) => u.id !== user.id)
        : allRecent;
      setRecentUsers(filtered);
    };

    loadRecentUsers();

    // Refresh recent users when user changes
    const interval = setInterval(loadRecentUsers, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // fetchUser is stable from Zustand store, no need to include in deps

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    try {
      localStorage.setItem('theme', theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const handlePasskeyRegistration = () => {
    setShowPasskeyRegistration(true);
  };

  const handleRegisterPasskey = async () => {
    try {
      await authProvider.auth.registerPasskey();
      // Simple notification instead of using notify
      console.log('Passkey registered successfully');
      setShowPasskeyRegistration(false);
    } catch (error) {
      console.error('Passkey registration error:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleLanguageChange = (newLocale: string) => {
    try {
      if (newLocale !== locale) {
        setLocale(newLocale);
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const handleThemeToggle = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const languages = [
    { locale: 'en', name: 'English', flag: '🇺🇸' },
    { locale: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  ];
  const currentLanguage =
    languages.find((lang) => lang.locale === locale) || languages[0];

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="gap-2">
            <User className="h-4 w-4" />
            <span className="hidden sm:inline" data-sensitive-field="userId">
              {user?.fullName || user?.name || user?.username || 'User'}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">
          {/* Mobile-only: Language Switcher */}
          <div className="md:hidden">
            <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
              {translate('pos.common.action.choose_language', 'Language')}
            </div>
            {languages.map((language) => (
              <DropdownMenuItem
                key={language.locale}
                onClick={() => handleLanguageChange(language.locale)}
                className={cn(
                  'flex items-center gap-2',
                  locale === language.locale && 'bg-accent'
                )}
              >
                <span className="text-xl">{language.flag}</span>
                <span className="flex-1">{language.name}</span>
                {locale === language.locale && (
                  <Badge
                    variant="outline"
                    className="h-5 w-5 p-0 flex items-center justify-center"
                  >
                    ✓
                  </Badge>
                )}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className="md:hidden" />
          </div>

          {/* Mobile-only: Theme Toggle */}
          <DropdownMenuItem onClick={handleThemeToggle} className="md:hidden">
            <div className="flex items-center gap-2 w-full">
              {theme === 'dark' ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
              <span>
                {translate('pos.common.action.toggle_theme', 'Toggle Theme')}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="md:hidden" />

          {/* Switch User */}
          <DropdownMenuItem
            onClick={() => {
              if (isMobile) {
                setShowQrScanner(true);
              } else {
                setShowQrLogin(true);
              }
            }}
          >
            <Users className="h-4 w-4 mr-2" />
            {translate('pos.auth.qr_login.switch_user', 'Switch User')}
          </DropdownMenuItem>

          {/* Recent Users */}
          {recentUsers.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                {translate('pos.auth.recent_users', 'Recent Users')}
              </div>
              {recentUsers.map((recentUser) => (
                <DropdownMenuItem
                  key={recentUser.id}
                  onClick={() => {
                    // When clicking a recent user, open QR login dialog
                    // They'll need to scan QR code again to log in
                    setShowQrLogin(true);
                  }}
                  className="flex items-center gap-2"
                >
                  {recentUser.avatar ? (
                    <img
                      src={recentUser.avatar}
                      alt={recentUser.fullName}
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <UserIcon className="h-4 w-4" />
                  )}
                  <span className="flex-1 truncate">{recentUser.fullName}</span>
                  <Clock className="h-3 w-3 text-muted-foreground" />
                </DropdownMenuItem>
              ))}
            </>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
            {translate('pos.auth.change_password')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handlePasskeyRegistration}>
            {translate('pos.auth.register_passkey')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleLogout}>
            {translate('pos.auth.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog
        open={showChangePassword}
        onClose={() => setShowChangePassword(false)}
      />

      <PasskeyRegistrationDialog
        open={showPasskeyRegistration}
        onClose={() => setShowPasskeyRegistration(false)}
        onRegister={handleRegisterPasskey}
      />

      <QRCodeLoginDialog
        open={showQrLogin}
        onClose={() => setShowQrLogin(false)}
        onSuccess={() => {
          setShowQrLogin(false);
        }}
      />
      <QRLoginScannerDialog
        open={showQrScanner}
        onClose={() => setShowQrScanner(false)}
        onSuccess={() => {
          setShowQrScanner(false);
        }}
      />
    </>
  );
};

const SidebarToggle = ({ onToggle }: { onToggle: () => void }) => {
  return (
    <Button variant="ghost" size="icon" onClick={onToggle}>
      <Menu className="h-5 w-5" />
    </Button>
  );
};

export const ModernHeader = ({
  onSidebarToggle,
}: {
  onSidebarToggle?: () => void;
}) => {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [isWizardManagerOpen, setIsWizardManagerOpen] = useState(false);
  const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
  const [isBugReportDialogOpen, setIsBugReportDialogOpen] = useState(false);
  const [isFaqDialogOpen, setIsFaqDialogOpen] = useState(false);
  const { activateBlur, deactivateBlur } = useSensitiveDataBlur();

  const handleOpenFeatureSearch = () => {
    setIsWizardManagerOpen(true);
  };

  const handleCloseFeatureSearch = () => {
    setIsWizardManagerOpen(false);
  };

  const handleSupportClick = () => {
    setIsSupportDialogOpen(true);
  };

  const handleOpenBugReport = async () => {
    setIsSupportDialogOpen(false);
    try {
      const { captureScreenshotWithPreview } = await import(
        '../utils/bugReportUtils'
      );
      console.log('[Bug Report] Starting capture (header)');
      // Activate blur before capture
      activateBlur();
      // Wait for blur to apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      await captureScreenshotWithPreview();

      // Deactivate blur after capture
      deactivateBlur();

      console.log('[Bug Report] Capture completed, opening dialog');
      setIsBugReportDialogOpen(true);
    } catch (error) {
      console.error('[Bug Report] Error in preview sequence:', error);
      deactivateBlur(); // Ensure blur is deactivated on error
    }
  };

  const handleOpenFaq = () => {
    setIsSupportDialogOpen(false);
    setIsFaqDialogOpen(true);
  };

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center px-4 md:px-8 overflow-x-hidden">
          <div className="mr-2 md:mr-4 hidden md:flex">
            <ModernLogo onClick={() => navigate('/dashboard')} />
          </div>

          {/* Sidebar Toggle - Desktop */}
          {onSidebarToggle && (
            <div className="hidden md:block">
              <SidebarToggle onToggle={onSidebarToggle} />
            </div>
          )}

          <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
            <div className="w-full flex-1 md:w-auto md:flex-none min-w-0">
              {/* Feature search entry */}
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start text-xs md:text-sm gap-2 text-muted-foreground"
                onClick={handleOpenFeatureSearch}
              >
                <Search className="h-4 w-4" />
                <span className="truncate">
                  {translate('pos.wizards.manager.searchPlaceholder')}
                </span>
              </Button>
            </div>

            <nav className="flex items-center space-x-1 md:space-x-2">
              {/* Role Simulation Indicator */}
              <div className="hidden md:block">
                <RoleSimulationIndicator />
              </div>

              {/* Support Button */}
              <Button
                variant="outline"
                size="sm"
                className="hidden sm:inline-flex items-center gap-1"
                onClick={handleSupportClick}
              >
                <LifeBuoy className="h-4 w-4" />
                <span className="text-xs md:text-sm">
                  {translate('pos.common.action.support')}
                </span>
              </Button>

              {/* Notifications */}
              <div className="relative">
                <NotificationButton />
              </div>

              {/* Language Switcher */}
              <div className="hidden md:block">
                <ModernLanguageSwitcher />
              </div>

              {/* Theme Toggle */}
              <div className="hidden md:block">
                <ThemeToggle />
              </div>

              {/* User Menu */}
              <UserMenu />
            </nav>
          </div>
        </div>
      </header>

      {/* Feature Wizards / Feature Search */}
      <WizardManager
        open={isWizardManagerOpen}
        onClose={handleCloseFeatureSearch}
      />

      {/* Support Dialog */}
      <Dialog open={isSupportDialogOpen} onOpenChange={setIsSupportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{translate('support.title', 'Support')}</DialogTitle>
            <DialogDescription>
              {translate(
                'support.description',
                'How can we help you? Choose an option below.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={handleOpenBugReport}
            >
              {translate('support.reportBug', 'Report a bug')}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start"
              onClick={handleOpenFaq}
            >
              {translate('support.viewFaq', 'View FAQ')}
            </Button>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsSupportDialogOpen(false)}
            >
              {translate('pos.common.action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bug Report Dialog */}
      <BugReportDialog
        open={isBugReportDialogOpen}
        onClose={() => setIsBugReportDialogOpen(false)}
      />

      {/* FAQ Dialog */}
      <Dialog open={isFaqDialogOpen} onOpenChange={setIsFaqDialogOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {translate('faq.title', 'Frequently Asked Questions')}
            </DialogTitle>
            <DialogDescription>
              {translate(
                'faq.description',
                'Here are some common questions about using NEKO POS.'
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div>
              <div className="font-medium">
                {translate('faq.howToReportBug.question')}
              </div>
              <p className="text-muted-foreground">
                {translate('faq.howToReportBug.answer')}
              </p>
            </div>

            <div>
              <div className="font-medium">
                {translate('faq.howToRequestFeature.question')}
              </div>
              <p className="text-muted-foreground">
                {translate('faq.howToRequestFeature.answer')}
              </p>
            </div>

            <div>
              <div className="font-medium">
                {translate('faq.urgentSupport.question')}
              </div>
              <p className="text-muted-foreground">
                {translate('faq.urgentSupport.answer')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsFaqDialogOpen(false)}
            >
              {translate('pos.common.action.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
